import i18n from '@/utils/i18n';
import React, { useMemo, useRef, useState } from 'react';
import { ActionToastContent } from '@/components/action-toast-content';
import { AlertPill } from './alert-pill';
import { Analytics } from '@/utils/analytics';
import { AnalyticsAlertStatusType } from '@/types';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import { getTranslationValue } from '@/utils/translations';
import { HubCardData } from '@/hooks/insights-hub/types';
import { InsightsHubActionSheet } from './insights-hub-action-sheet';
import {
  Platform,
  Pressable,
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { RestaurantInfoHeader } from './restaurant-info-header';
import { SCROLL_DELAY } from '@/components/list-item';
import { SupportInfo } from './support-info';
import { TouchableEllipsis } from './touchable-ellipsis';
import { useAnalyticsAlertActions } from '@/hooks/insights-hub/use-analytics-alerts-actions';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useHover } from 'react-native-web-hooks';
import { useLayoutContext } from '@/contexts/layout-context';
import { useToasts } from '@/contexts/toast-context';

export interface Props {
  children?: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  index: number;
  item: HubCardData;
  onPress: () => void;
  onPressSupportInfo: () => void;
}

export const AlertCard: React.FC<Props> = ({
  children,
  containerStyle,
  index,
  item,
  onPress,
  onPressSupportInfo,
}) => {
  const [styles] = useStyles();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const { showToast, showErrorToast } = useToasts();
  const { selectedRestaurantRelationship } = useAuthenticatedUserContext();
  const {
    hideActiveAlerts,
    unhideActiveAlerts,
    deleteActiveAlerts,
    deleteHiddenAlerts,
    undeleteActiveAlerts,
    undeleteHiddenAlerts,
  } = useAnalyticsAlertActions(selectedRestaurantRelationship.supplierUUID);

  const ellipsisRef = useRef<React.ComponentRef<typeof TouchableOpacity>>(null);
  const alertCardRef =
    useRef<React.ComponentRef<typeof TouchableOpacity>>(null);
  const isAlertCardHovered = useHover(alertCardRef);

  const [showMoreActionSheet, setShowMoreActionSheet] = useState(false);
  const [onCardPress, setOnCardPress] = useState(false);

  const {
    alert_status: alertStatus,
    alert_uuid: alertUUID,
    card_type: cardType,
    restaurant_uuid: restaurantUUID,
    description,
    subtitle,
    title,
  } = item;

  const cardTrackingData = useMemo(
    () => ({
      alertType: cardType,
      alertUUID,
      platform: Platform.OS,
      restaurantUUID,
    }),
    [cardType, alertUUID, restaurantUUID],
  );

  const onPressMoreActions = () => {
    setShowMoreActionSheet(true);
    Analytics.track('insights_hub.more_actions_button.press', cardTrackingData);
  };

  const onCloseMoreActions = () => {
    setShowMoreActionSheet(false);
  };

  const showUndoHideToast = async () => {
    showToast({
      content: (onClose) => (
        <ActionToastContent
          buttonProps={{
            children: i18n.t('undo'),
            onPress: async () => {
              onClose();
              await unhideActiveAlerts([item], index);
              Analytics.track(
                'insights_hub.action_sheet.hide.undo.press',
                cardTrackingData,
              );
            },
            trackingData: null,
          }}
          textContent={i18n.t('dsr_analytics.alert_hidden')}
        />
      ),
    });
  };

  const onHide = async () => {
    try {
      onCloseMoreActions();
      await hideActiveAlerts([alertUUID]);
      Analytics.track('insights_hub.action_sheet.hide.press', cardTrackingData);
      await showUndoHideToast();
    } catch (e) {
      showErrorToast();
    }
  };

  const showUndoDeleteToast = async (alertStatus: AnalyticsAlertStatusType) => {
    showToast({
      content: (onClose) => (
        <ActionToastContent
          buttonProps={{
            children: i18n.t('undo'),
            onPress: async () => {
              onClose();
              if (alertStatus === AnalyticsAlertStatusType.Active) {
                await undeleteActiveAlerts([item], index);
              }
              if (alertStatus === AnalyticsAlertStatusType.Hidden) {
                await undeleteHiddenAlerts([item], index);
              }
              Analytics.track(
                'insights_hub.action_sheet.delete.undo.press',
                cardTrackingData,
              );
            },
            trackingData: null,
          }}
          textContent={i18n.t('dsr_analytics.alert_removed')}
        />
      ),
    });
  };

  const onDelete = async () => {
    onCloseMoreActions();
    try {
      if (alertStatus === AnalyticsAlertStatusType.Active) {
        await deleteActiveAlerts([alertUUID]);
        Analytics.track(
          'insights_hub.action_sheet.delete.press',
          cardTrackingData,
        );
        await showUndoDeleteToast(item.alert_status);
      }
      if (alertStatus === AnalyticsAlertStatusType.Hidden) {
        await deleteHiddenAlerts([alertUUID]);
        Analytics.track(
          'insights_hub.action_sheet.delete.press',
          cardTrackingData,
        );
        await showUndoDeleteToast(item.alert_status);
      }
    } catch (e) {
      showErrorToast();
    }
  };

  const isSmallTabletOrSmaller = isSmallLayout || isSmallTabletLayout;

  // when on mobile, mobile web, or tablet, always show the ellipsis
  // when on desktop web, show the ellipsis only when the card is hovered
  const shouldShowEllipsis =
    Platform.OS !== 'web' ||
    isSmallTabletOrSmaller ||
    isAlertCardHovered ||
    showMoreActionSheet;

  return (
    <>
      <Pressable
        delayLongPress={SCROLL_DELAY}
        onPress={onPress}
        onPressIn={() => {
          setOnCardPress(true);
        }}
        onPressOut={() => {
          setOnCardPress(false);
        }}
        ref={alertCardRef}
        style={[styles.card, containerStyle]}
      >
        {/* Background press effect container */}
        <View
          style={[
            styles.contentWithBackground,
            onCardPress ? styles.contentWithBackgroundPressed : null,
          ]}
        >
          <View style={styles.topRow}>
            <AlertPill alertType={cardType} />
            <TouchableEllipsis
              onPressMore={onPressMoreActions}
              ref={ellipsisRef}
              style={
                shouldShowEllipsis
                  ? styles.ellipsisDisplayed
                  : styles.ellipsisHidden
              }
            />
          </View>
          <RestaurantInfoHeader
            restaurantUUID={restaurantUUID}
            subtitle={subtitle}
            title={title}
          />
          {description ? (
            <Text style={[FONTS.primary, styles.description]}>
              {getTranslationValue(description)}
            </Text>
          ) : null}
        </View>
        <SupportInfo
          item={item}
          onPress={onPressSupportInfo}
          style={styles.transparentSection}
        />
        {children}
      </Pressable>
      {showMoreActionSheet ? (
        <InsightsHubActionSheet
          onClose={onCloseMoreActions}
          onDeleteAlert={onDelete}
          onHideAlert={
            // if the alert is already hidden, don't show the hide alert option
            item.alert_status !== AnalyticsAlertStatusType.Hidden
              ? onHide
              : undefined
          }
          targetRef={ellipsisRef}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.sk.color.bg.primary.default,
    gap: 8,
    paddingBottom: 16,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  contentWithBackground: {
    backgroundColor: theme.sk.color.bg.primary.default,
    gap: 8,
  },
  contentWithBackgroundPressed: {
    // consistency - 0.2 is the default activeOpacity for TouchableOpacity
    opacity: 0.2,
  },
  description: {
    color: theme.sk.color.text.secondary,
    fontSize: 14,
    letterSpacing: 0.05,
    lineHeight: 21,
  },
  ellipsisDisplayed: {
    opacity: 1,
  },
  ellipsisHidden: {
    opacity: 0,
  },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  transparentSection: {
    backgroundColor: 'transparent',
  },
}));
